import { Link } from "react-router-dom";
import { ArrowRight, Sprout, Home, Wifi, Trees, Users } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useSiteSection } from "@/lib/useSiteSection";
import { buildBreadcrumb } from "@/lib/schemas";

const STUDENT_TIERS = [
  { service_type: "student-onsite",  durations: [{ min: 60, price: 200 }, { min: 90, price: 250 }],
    icon: Home,  title: "Student 1-on-1 · On-Site",
    sub: "At the homestead in Chino Valley, AZ.",
    includes: "Tailored curriculum packet for ongoing learning at home." },
  { service_type: "student-athome",  durations: [{ min: 60, price: 250 }, { min: 90, price: 300 }],
    icon: Trees, title: "Student 1-on-1 · At Home",
    sub: "Heather travels to your home. No waiver required.",
    includes: "Tailored curriculum packet + travel fee calculated by address.",
    travelNote: true },
  { service_type: "student-virtual", durations: [{ min: 60, price: 125 }, { min: 90, price: 165 }],
    icon: Wifi,  title: "Student 1-on-1 · Virtual",
    sub: "Live online tutoring — meeting link sent after confirmation.",
    includes: "Tailored digital curriculum + recording of session." },
];

const ADULT_TIERS = [
  { service_type: "adult-onsite",  durations: [{ min: 60, price: 175 }, { min: 90, price: 225 }],
    icon: Home,  title: "Adult 1-on-1 · On-Site",
    sub: "At the homestead in Chino Valley, AZ.",
    includes: "Tailored resources + discounted virtual coaching for follow-up." },
  { service_type: "adult-athome",  durations: [{ min: 60, price: 225 }, { min: 90, price: 275 }],
    icon: Trees, title: "Adult 1-on-1 · At Client Location",
    sub: "Heather travels to your home or business.",
    includes: "Tailored resources + discounted virtual coaching. Travel fee calculated by address.",
    travelNote: true },
  { service_type: "adult-virtual", durations: [{ min: 60, price: 110 }, { min: 90, price: 145 }],
    icon: Wifi,  title: "Adult 1-on-1 · Virtual",
    sub: "Live online coaching — meeting link sent after confirmation.",
    includes: "Tailored digital resources + recording." },
];

export default function ServicesPage() {
  const hero = useSiteSection("services_hero", {
    eyebrow: "Coaching & consultations",
    heading: "Pick a path that fits your learner — or your land.",
    body: "Every 1-on-1 session is built around what you want to grow next, with a take-home packet so the learning continues after you leave. Property consultations are walked on-site at your address.",
  });
  return (
    <>
      <SEO
        title="1-on-1 Coaching, Consultations & Private Group Classes"
        description="Private homestead coaching for students and adults in Chino Valley, AZ. On-site, at-home, or virtual. Property consultations across Yavapai County."
        keywords="Arizona homestead consulting, 1-on-1 homeschool tutoring, Chino Valley permaculture consultation, private group homestead class"
        jsonLd={buildBreadcrumb([
          { name: "Home", url: "/" },
          { name: "1-on-1 & Consulting", url: "/services" },
        ])}
      />
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-20">
        <header className="max-w-3xl fade-up">
          <p className="uppercase tracking-[0.22em] text-xs sm:text-sm text-gg-brown font-semibold">
            {hero.eyebrow}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-text)] leading-[1.05] mt-3">
            {hero.heading}
          </h1>
          <p className="mt-5 text-gg-muted text-lg leading-relaxed">
            {hero.body}
          </p>
        </header>

        {/* STUDENT TIERS */}
        <Section
          eyebrow="For students — ESA / homeschooled"
          icon={Sprout}
          title="Student 1-on-1 Coaching"
          blurb="Open to ESA ClassWallet students and traditional homeschoolers. Tell us the subject and Heather builds the hour."
          tiers={STUDENT_TIERS}
        />

        {/* ADULT TIERS */}
        <Section
          eyebrow="For adults"
          icon={Users}
          title="Adult 1-on-1 Coaching"
          blurb="One-on-one coaching for the home gardener, hobby farmer, or food-skill curious."
          tiers={ADULT_TIERS}
        />

        {/* CONSULTATION */}
        <div className="mt-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 fade-up">
            <p className="uppercase tracking-[0.18em] text-xs text-gg-brown font-semibold">
              For your land
            </p>
            <h2 className="font-heading text-4xl text-[var(--gg-text)] leading-tight mt-3">
              Homestead & Permaculture Consultation
            </h2>
            <p className="mt-5 text-gg-muted leading-relaxed">
              On-location consulting for homeowners, homesteaders, and local businesses building productive, sustainable outdoor spaces. We'll walk your land together — soil, sun, water — and you'll receive a clear, prioritized PDF summary within 3–5 days.
            </p>
          </div>
          <div className="lg:col-span-7 fade-up d1">
            <div className="rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8">
              <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
                Base packages
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-center">
                {[
                  { h: "1 hr", p: 300 },
                  { h: "2 hr", p: 500 },
                  { h: "3 hr", p: 700 },
                ].map((t) => (
                  <div key={t.h} className="rounded-xl bg-gg-bg border border-gg-border p-4">
                    <p className="font-heading text-2xl text-gg-green">{t.h}</p>
                    <p className="text-sm text-gg-muted mt-1">${t.p}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gg-muted mt-3">
                Includes the Property Visit Summary PDF (observations, next-steps checklist, resource &amp; plant list).
              </p>

              <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mt-7">
                Optional add-ons
              </p>
              <ul className="mt-3 space-y-3 text-sm">
                <li className="rounded-xl bg-gg-bg border border-gg-border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold">Backyard Layout</p>
                    <span className="font-mono text-gg-brown">+$175</span>
                  </div>
                  <p className="text-gg-muted mt-1">Best for suburban lots — color-coded satellite map, plant list, seasonal checklist.</p>
                </li>
                <li className="rounded-xl bg-gg-bg border border-gg-border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold">Homestead Framework</p>
                    <span className="font-mono text-gg-brown">+$350</span>
                  </div>
                  <p className="text-gg-muted mt-1">For 1–5 acres — permaculture map, 12-month workflow, ecosystem strategy.</p>
                </li>
                <li className="rounded-xl bg-gg-bg border border-gg-border p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold">Large Acreage Plan (5+ acres)</p>
                    <span className="font-mono text-gg-brown">Custom quote</span>
                  </div>
                  <p className="text-gg-muted mt-1">Water management, orchard / food-forest design, multi-year buildout in affordable phases.</p>
                </li>
              </ul>

              <Link
                to="/consultation"
                className="btn-pill btn-primary mt-7 w-full justify-center"
                data-testid="services-consultation-cta"
              >
                Request A Consultation <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* GROUP CLASS + VIRTUAL CONSULTING */}
        <div className="mt-20 grid md:grid-cols-2 gap-6">
          <CalloutCard
            eyebrow="Private group"
            title="Private Group Class"
            body="Homeschool co-ops, traditional schools, friend groups, book clubs, moms' groups, girls' nights — make-and-take classes built around your group's vibe."
            cta="Request A Group Class"
            to="/group-class"
            testid="services-group-class-cta"
          />
          <CalloutCard
            eyebrow="Virtual consulting"
            title="Virtual Consulting"
            body="Already on a homestead journey and need a sounding board? Book a virtual deep-dive with Heather — pricing depends on scope. Reach out and we'll tailor it."
            cta="Contact Heather"
            to="mailto:Heather@gracefulgardencoaching.com?subject=Virtual%20consulting%20inquiry"
            external
            testid="services-virtual-consulting-cta"
          />
        </div>
      </main>
      <Footer />
    </>
  );
}

function Section({ eyebrow, icon: Icon, title, blurb, tiers }) {
  return (
    <section className="mt-20">
      <div className="flex items-start gap-4 max-w-3xl">
        <span className="rounded-full bg-gg-green/10 p-3 mt-1"><Icon className="text-gg-green" size={22} /></span>
        <div>
          <p className="uppercase tracking-[0.18em] text-xs text-gg-brown font-semibold">{eyebrow}</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] mt-1 leading-tight">{title}</h2>
          <p className="mt-3 text-gg-muted leading-relaxed">{blurb}</p>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-5">
        {tiers.map((t) => <TierCard key={t.service_type} tier={t} />)}
      </div>
    </section>
  );
}

function TierCard({ tier }) {
  const TierIcon = tier.icon;
  return (
    <article className="card-lift rounded-2xl border border-gg-border bg-gg-surface p-6 flex flex-col">
      <div className="flex items-center justify-between">
        <TierIcon className="text-gg-green" size={20} />
        {tier.travelNote && (
          <span className="text-[10px] uppercase tracking-[0.16em] text-gg-brown font-semibold bg-gg-brown/8 px-2 py-1 rounded-full">
            + travel fee
          </span>
        )}
      </div>
      <h3 className="font-heading text-xl text-[var(--gg-text)] mt-4 leading-tight">{tier.title}</h3>
      <p className="text-sm text-gg-muted mt-2">{tier.sub}</p>

      <div className="mt-5 space-y-2">
        {tier.durations.map((d) => (
          <Link
            key={d.min}
            to={`/book?service=${tier.service_type}&duration=${d.min}`}
            className="flex items-center justify-between rounded-xl border border-gg-border bg-gg-bg px-4 py-3 hover:border-gg-green/60 hover:bg-gg-green/5 transition-all"
            data-testid={`tier-${tier.service_type}-${d.min}`}
          >
            <span className="font-semibold text-[var(--gg-text)]">{d.min} min</span>
            <span className="flex items-center gap-3">
              <span className="font-heading text-xl text-gg-green">${d.price}</span>
              <ArrowRight size={14} className="text-gg-brown" />
            </span>
          </Link>
        ))}
      </div>

      <p className="text-xs text-gg-muted mt-5 leading-relaxed border-t border-gg-border pt-4">
        <span className="font-semibold text-gg-brown">Includes:</span> {tier.includes}
      </p>
    </article>
  );
}

function CalloutCard({ eyebrow, title, body, cta, to, external, testid }) {
  const linkProps = external
    ? { href: to, target: "_self", rel: "noreferrer" }
    : null;
  const Wrapper = external ? "a" : Link;
  const wrapperProps = external ? linkProps : { to };
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-7 sm:p-8 flex flex-col">
      <p className="uppercase tracking-[0.18em] text-xs text-gg-brown font-semibold">{eyebrow}</p>
      <h3 className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] mt-3 leading-tight">{title}</h3>
      <p className="mt-4 text-gg-muted leading-relaxed">{body}</p>
      <Wrapper {...wrapperProps} className="btn-pill btn-outline mt-6 self-start" data-testid={testid}>
        {cta} <ArrowRight size={14} />
      </Wrapper>
    </div>
  );
}
